// EmailValueAdd.js
// Vonzás 02 · Email Varázsló · 2. kártya — Értékadó email.
//
// REUSE STRATÉGIA:
//   - A `setup` fázis (topic chips + saját téma textarea + CTA selector)
//     az AttractionAgents.js-ben él INLINE az Email Varázsló kártya alatt
//     (mint a Welcome 3 kérdése).
//   - A `sketches` fázis az EXISTING poszt outline page-et használja:
//     krém doc + Posztvázlatok-stílusú vertikális lista + CreamSketchSection.
//   - A `result` fázis a Welcome-stílusú cream email frame-et használja
//     (TÁRGYSOR-OPCIÓK pillek + fehér body card + lila refine panel).
//
// PROPS:
//   - initialSketches: [{id, title, preview, ...}, ...] — már lekért 3 vázlat
//   - tema, ctaTipus, valasztottTopicObject — a setup-ból átörökítve

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  CircleNotch, Copy as CopyIcon,
  DownloadSimple, CheckCircle, Paperclip, PaperPlaneTilt,
  X as XIcon,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";
import { getActiveAlap } from "../lib/activeAlap";
import { CreamSketchSection } from "./MagnetPostFacebook";

const ACCENT_BLUE = "#26A9C9";
const ACCENT_BLUE_DARK = "#135b6c";
const ACCENT_BLUE_TINT_PILL = "rgba(38, 169, 201, 0.14)";
const ACCENT_BLUE_TINT_BLOCK = "rgba(38, 169, 201, 0.10)";
const ACCENT_BLUE_BORDER_BLOCK = "rgba(38, 169, 201, 0.30)";
const SUBJECT_PILL_BG = "rgba(38, 169, 201, 0.08)";
const SUBJECT_PILL_BORDER = "rgba(38, 169, 201, 0.35)";

const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb" },
  { id: "soft",     label: "Lágyabb" },
  { id: "concrete", label: "Konkrétabb" },
  { id: "story",    label: "Több sztori" },
  { id: "humor",    label: "Humorosabb" },
  { id: "surprise", label: "Lepj meg!" },
];

const PLAIN_BRACKETS = new Set([
  "keresztnév", "keresztneved",
  "név", "neved",
  "a te neved", "a neved", "coachod neve", "coach neve",
  "aláírás", "signature",
]);

export default function EmailValueAdd({
  initialSketches = [],
  tema = "",
  ctaTipus = "",
  valasztottTopicObject = null,
}) {
  // phase: sketches | result
  const [phase, setPhase] = useState(initialSketches.length > 0 ? "sketches" : "sketches");
  const [vazlatok, setVazlatok] = useState(initialSketches);
  const [chosenSketch, setChosenSketch] = useState(null);
  const [expanding, setExpanding] = useState(false);

  // Sketch-fázis refine (re-roll) state
  const [sketchBusy, setSketchBusy] = useState(false);
  const [sketchChat, setSketchChat] = useState("");
  const [seenSketchTitles, setSeenSketchTitles] = useState(
    () => (initialSketches || []).map((v) => (v.title || v.horog || "").trim()).filter(Boolean)
  );

  const [content, setContent] = useState(null);
  const [rawText, setRawText] = useState("");
  const [chosenSubjects, setChosenSubjects] = useState({});

  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingVault, setSavingVault] = useState(false);

  const [selection, setSelection] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle");
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const previewRef = useRef(null);

  const activeAlap = useMemo(() => getActiveAlap(), []);

  // ── Step: Pick sketch → /email/valueadd/full ─────────────────────
  const handleExpand = async (sketch) => {
    if (expanding) return;
    setExpanding(true);
    setChosenSketch(sketch);
    try {
      const res = await api.post("/attraction/email/valueadd/full", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        cta_tipus: ctaTipus,
        valasztott_vazlat: sketch,
      });
      const txt = (res.data?.teljes_szoveg || "").trim();
      const vc = res.data?.valueadd_content || { emails: [] };
      if (!txt) {
        toast.error("Üres válasz. Próbáld újra.");
        return;
      }
      setRawText(txt);
      setContent(Array.isArray(vc.emails) && vc.emails.length > 0 ? vc : null);
      const defaults = {};
      (vc.emails || []).forEach((e) => { defaults[e.email_szam] = 0; });
      setChosenSubjects(defaults);
      setPhase("result");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Az e-mail írása sikertelen.");
    } finally {
      setExpanding(false);
    }
  };

  // ── Sketch re-roll: kérj új vázlatokat instrukcióval ─────────────
  const regenerateSketches = async (instruction) => {
    if (sketchBusy) return;
    setSketchBusy(true);
    try {
      const res = await api.post("/attraction/post-facebook/sketches", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        cta_tipus: ctaTipus,
        aktiv_poszt: null,
        topic_object: valasztottTopicObject || null,
        user_instruction: instruction || "",
        previous_sketch_titles: seenSketchTitles,
      });
      const vs = Array.isArray(res.data?.vazlatok) ? res.data.vazlatok : [];
      if (!vs.length) {
        toast.error("Nem érkezett új vázlat. Próbáld újra.");
        return;
      }
      setVazlatok(vs);
      setSeenSketchTitles((prev) => Array.from(new Set([
        ...prev,
        ...vs.map((v) => (v.title || v.horog || "").trim()).filter(Boolean),
      ])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Új vázlatok lekérése sikertelen.");
    } finally {
      setSketchBusy(false);
    }
  };

  // ── Composed full text ───────────────────────────────────────────
  const composedAllText = useMemo(() => {
    if (!content?.emails?.length) return rawText || "";
    return content.emails.map((em) => {
      const idx = chosenSubjects[em.email_szam] ?? 0;
      const subjects = em.targysorok || [];
      const lines = [];
      if (subjects.length) {
        const reordered = [subjects[idx] || subjects[0], ...subjects.filter((_, i) => i !== idx)];
        lines.push("TÁRGYSOR-OPCIÓK:");
        reordered.forEach((t, i) => lines.push(`${i + 1}. ${t}`));
      }
      lines.push("");
      lines.push(em.body || "");
      return lines.join("\n");
    }).join("\n\n");
  }, [content, chosenSubjects, rawText]);

  // ── Selection capture ────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "result") return undefined;
    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      setSelection(text.slice(0, 600));
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [phase]);

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // ── Refine pipeline ──────────────────────────────────────────────
  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride) setChatInput("");
    try {
      const res = await api.post("/attraction/email/valueadd/refine", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        cta_tipus: ctaTipus,
        valasztott_vazlat: chosenSketch || null,
        jelenlegi_sorozat: composedAllText,
        utasitas: utasitas || "",
        quote: quoteToSend || null,
        quick_button: quickButtonId || null,
      });
      const data = res.data || {};
      const mode = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion({
        mod: mode,
        snippet: data.snippet || "",
        teljes: data.teljes_szoveg || "",
        content: data.valueadd_content || null,
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch {
      setSuggestion({
        mod: "error",
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet =
      suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || "");
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await api.post("/attraction/email/valueadd/refine", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        cta_tipus: ctaTipus,
        valasztott_vazlat: chosenSketch || null,
        jelenlegi_sorozat: composedAllText,
        utasitas: (suggestion.instr || "") +
          "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt." +
          (previousSnippet ? ` Az előző javaslat eleje így szólt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        quote: suggestion.quote || null,
      });
      const data = res.data || {};
      const mode = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion((s) => ({
        ...s,
        mod: mode,
        snippet: data.snippet || "",
        teljes: data.teljes_szoveg || "",
        content: data.valueadd_content || null,
      }));
    } catch {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const prevText = composedAllText;
    let nextText = prevText;
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      const idx = prevText.indexOf(suggestion.quote);
      if (idx >= 0) {
        nextText = prevText.slice(0, idx) + suggestion.snippet + prevText.slice(idx + suggestion.quote.length);
      } else {
        setRefinePhase("idle");
        setSuggestion(null);
        return;
      }
    } else if (suggestion.mod === "teljes" && suggestion.teljes) {
      nextText = suggestion.teljes;
    } else {
      setRefinePhase("idle");
      setSuggestion(null);
      return;
    }
    setUndoSnapshot({ content, rawText, chosenSubjects });
    setRawText(nextText);
    if (suggestion.mod === "snippet") {
      if (content && Array.isArray(content.emails)) {
        const nextEmails = content.emails.map((em) => {
          const oldBody = em.body || "";
          if (oldBody.includes(suggestion.quote)) {
            return { ...em, body: oldBody.replace(suggestion.quote, suggestion.snippet) };
          }
          return em;
        });
        setContent({ ...content, emails: nextEmails });
      }
    } else {
      const nextContent = suggestion.content || null;
      const nextEmails = Array.isArray(nextContent?.emails) ? nextContent.emails : [];
      if (nextEmails.length > 0) {
        setContent(nextContent);
        const next = {};
        nextEmails.forEach((e) => { next[e.email_szam] = chosenSubjects[e.email_szam] ?? 0; });
        setChosenSubjects(next);
      } else {
        setContent(null);
      }
    }
    setSelection(null);
    setSaved(false);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setContent(undoSnapshot.content);
    setRawText(undoSnapshot.rawText);
    setChosenSubjects(undoSnapshot.chosenSubjects);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const handleSubmitChat = () => requestRefine();

  // ── Cream actions ────────────────────────────────────────────────
  const handleCopy = async () => {
    if (!composedAllText) return;
    try {
      await navigator.clipboard.writeText(composedAllText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Vágólapra másolva");
    } catch { toast.error("Másolás nem sikerült"); }
  };

  const handleCopyOne = async (em) => {
    if (!em || !em.body) return;
    try {
      await navigator.clipboard.writeText(em.body);
      toast.success("Levél másolva");
    } catch { toast.error("Másolás nem sikerült"); }
  };

  const handleDownload = () => {
    if (!composedAllText) return;
    const blob = new Blob([composedAllText], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ertekado-email.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const handleSave = async () => {
    if (!composedAllText || saved || savingVault) return;
    setSavingVault(true);
    // A Tudástár entry címe = az első e-mail kiválasztott tárgysora.
    // Ettől találhatóvá válik a mentett levél a listanézetben.
    const firstEmail = content?.emails?.[0];
    const subjects = firstEmail?.targysorok || [];
    const idx = chosenSubjects[firstEmail?.email_szam] ?? 0;
    const subjectTitle = (subjects[idx] || subjects[0] || "").trim();
    const saveName = subjectTitle || (tema || "").trim() || "Értékadó email";
    try {
      await api.post("/attraction/email/valueadd/save", {
        name: saveName,
        teljes_szoveg: composedAllText,
        valueadd_content: content || null,
        chosen_subjects: chosenSubjects,
        tema: (tema || "").trim(),
        cta_tipus: ctaTipus,
        valasztott_vazlat: chosenSketch || null,
      });
      setSaved(true);
      toast.success("Mentve a Tudástárba");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen.");
    } finally {
      setSavingVault(false);
    }
  };

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion;
  const showApplied = refinePhase === "applied";
  const inputDisabled = isLoading || showSuggestion;

  // ─────────────── PHASE: SKETCHES (REUSE poszt outline page) ─────────
  // Két-paneles layout — IDENTIKUS a MagnetPostFacebook sketch-fázisával:
  // LEFT = lila refine panel (sketch instrukciók + Új vázlatok gomb)
  // RIGHT = krém doc a CreamSketchSection-ökkel.
  if (phase === "sketches") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="flex flex-col h-full"
        style={{ background: "#0a0a12" }}
        data-testid="email-valueadd-shell"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
          {/* LEFT — Sketch-fázis refine panel */}
          <div
            className="flex flex-col border-r min-h-0 h-full"
            style={{ borderColor: "rgba(255,255,255,0.04)" }}
            data-testid="email-valueadd-sketches-finomito"
          >
            <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
              <div style={{ maxWidth: 480 }}>
                <div
                  className="font-heading"
                  style={{
                    fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                    letterSpacing: "-0.015em", color: "#ffffff", marginBottom: 18,
                  }}
                >
                  Válassz egy vázlatot.
                </div>
                <div style={{ fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0" }}>
                  Három különböző irányból építettünk vázlatot ehhez a témához. Kattints a kártyára jobboldalt — abból írom meg a teljes e-mailt. Ha más szögből szeretnéd, kérd lent.
                </div>
              </div>

              {sketchBusy && (
                <div
                  className="flex items-center gap-2.5"
                  style={{
                    fontSize: 13, color: "#a8a8c0", padding: "4px 2px",
                    marginTop: 32,
                  }}
                  data-testid="email-valueadd-sketches-busy"
                >
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                  Új vázlatok készülnek…
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (sketchChat.trim() && !sketchBusy) {
                  regenerateSketches(sketchChat.trim());
                  setSketchChat("");
                }
              }}
              style={{ padding: "12px 24px 24px" }}
              data-testid="email-valueadd-sketches-form"
            >
              <div style={{
                background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                border: "1px solid rgba(243,238,228,0.14)",
                borderRadius: 12,
                padding: "5px 5px 5px 14px",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                opacity: sketchBusy ? 0.5 : 1,
                pointerEvents: sketchBusy ? "none" : "auto",
              }}>
                <button type="button" aria-label="Csatolás"
                  style={{
                    background: "transparent", border: "none", padding: 0,
                    color: "#8a8a9c", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                  <Paperclip size={14} weight="regular" />
                </button>
                <input
                  value={sketchChat}
                  onChange={(e) => setSketchChat(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (sketchChat.trim() && !sketchBusy) {
                        regenerateSketches(sketchChat.trim());
                        setSketchChat("");
                      }
                    }
                  }}
                  placeholder={`Pl.: „adj 3 vázlatot, ami metaforából indul"`}
                  data-testid="email-valueadd-sketches-chat"
                  disabled={sketchBusy}
                  className="flex-1 outline-none"
                  style={{
                    background: "transparent", border: "none", color: "white",
                    fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                  }}
                />
                <button
                  type="submit"
                  disabled={!sketchChat.trim() || sketchBusy}
                  data-testid="email-valueadd-sketches-send"
                  aria-label="Új vázlatok"
                  style={{
                    width: 36, height: 36,
                    background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                    color: "white", borderRadius: 9, border: "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: sketchChat.trim() && !sketchBusy ? "pointer" : "not-allowed",
                    opacity: sketchChat.trim() && !sketchBusy ? 1 : 0.5,
                    boxShadow: sketchChat.trim() && !sketchBusy
                      ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                      : "none",
                  }}
                >
                  {sketchBusy ? (
                    <CircleNotch size={15} weight="bold" className="animate-spin" />
                  ) : (
                    <PaperPlaneTilt size={15} weight="fill" />
                  )}
                </button>
              </div>
              <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
                <span>Vagy egyszerűen kattints a jobboldali vázlatok közül egyre.</span>
                <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
              </div>
            </form>
          </div>

          {/* RIGHT — krém doc a meglévő CreamSketchSection-ökkel */}
          <div
            className="overflow-y-auto h-full"
            style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
          >
            <div
              data-preview-theme="cream"
              data-testid="email-valueadd-sketches-cream"
              className="mr-auto"
              style={{
                background: "#f3eee4",
                color: "#0a0a12",
                borderRadius: 10,
                boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
                padding: "32px 44px 56px",
                maxWidth: 860,
                marginLeft: 0,
                position: "relative",
                fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
              }}
            >
              <h2
                className="font-heading"
                style={{
                  fontSize: 26, fontWeight: 700, color: "#0a0a12",
                  letterSpacing: "-0.018em", marginBottom: 10, lineHeight: 1.2,
                }}
              >
                Email vázlatok
              </h2>
              <p style={{ fontSize: 14, color: "#6a6a78", lineHeight: 1.6, marginBottom: 22, fontWeight: 400 }}>
                Három különböző irányból építettünk vázlatot ehhez a témához. Válaszd ki, melyiket szeretnéd teljes e-maillé írni. Kattints a kártyára.
              </p>
              <div data-testid="email-valueadd-sketches">
                {vazlatok.map((v, idx) => (
                  <CreamSketchSection
                    key={v.id || idx}
                    sketch={v}
                    index={idx}
                    isLast={idx === vazlatok.length - 1}
                    expanding={expanding && chosenSketch?.id === v.id}
                    onClick={() => handleExpand(v)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ─────────────── PHASE: RESULT (Welcome-style email frame) ─────────
  const hasStructured = content?.emails?.length > 0;
  const placeholder = selection
    ? "Mit változtassak a kijelölt részen?"
    : "Mit szeretnél finomítani?";
  const suggestionSnippet = suggestion
    ? (suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || ""))
    : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex flex-col h-full"
      style={{ background: "#0a0a12" }}
      data-testid="email-valueadd-shell"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* LEFT — Finomító Panel */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="email-valueadd-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                  letterSpacing: "-0.015em", color: "#ffffff", marginBottom: 18,
                }}
              >
                Elkészült az e-mail.
              </div>
              <div style={{ fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0" }}>
                Olvasd át a jobb oldalon. Ha minden rendben, mentsd el a Tudástárba. Ha valamin finomítanál, jelölj ki egy részt a szövegben, vagy írd lent, mit változtassak.
              </div>
            </div>

            <AnimatePresence>
              {(selection || isLoading || showSuggestion) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="email-valueadd-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: "2px solid rgba(124,108,242,0.40)",
                      borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                    }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>KIJELÖLT RÉSZ</div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button
                            type="button" onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="email-valueadd-selection-clear"
                          ><XIcon size={11} weight="bold" /></button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && !showSuggestion && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="email-valueadd-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id} type="button"
                          onClick={() => requestRefine(null, qa.id)}
                          data-testid={`email-valueadd-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {isLoading && (
                    <div className="flex items-center gap-2.5"
                         style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                         data-testid="email-valueadd-refine-loading">
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {showSuggestion && (
                    <div data-testid="email-valueadd-suggestion">
                      <SuggestionCard
                        snippet={suggestionSnippet}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showApplied && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="email-valueadd-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (chatInput.trim() && !inputDisabled) handleSubmitChat();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="email-valueadd-finomito-form"
          >
            <div style={{
              background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
              border: "1px solid rgba(243,238,228,0.14)",
              borderRadius: 12,
              padding: "5px 5px 5px 14px",
              display: "flex", alignItems: "center", gap: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
              opacity: inputDisabled ? 0.5 : 1,
              pointerEvents: inputDisabled ? "none" : "auto",
            }}>
              <button type="button" aria-label="Csatolás"
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "#8a8a9c", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}>
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (chatInput.trim() && !inputDisabled) handleSubmitChat();
                  }
                }}
                placeholder={placeholder}
                data-testid="email-valueadd-chat-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{
                  background: "transparent", border: "none", color: "white",
                  fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || inputDisabled}
                data-testid="email-valueadd-chat-send"
                aria-label="Küldés"
                style={{
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white", borderRadius: 9, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                  opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                  boxShadow: chatInput.trim() && !inputDisabled
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                }}
              >
                {isLoading ? (
                  <CircleNotch size={15} weight="bold" className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={15} weight="fill" />
                )}
              </button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
              <span>Tipp: jelölj ki egy részt jobboldalt - a kérésed arra fog vonatkozni.</span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* RIGHT — krém dokumentum */}
        <div
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            ref={previewRef}
            data-preview-theme="cream"
            data-testid="email-valueadd-cream-card"
            className="mr-auto cream-selectable"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
              fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
            }}
          >
            <div className="flex justify-end items-center"
                 style={{ gap: 6, marginBottom: 28, position: "relative", zIndex: 1 }}>
              <CreamActionPill onClick={handleCopy}
                icon={copied ? CheckCircle : CopyIcon}
                label={copied ? "Másolva" : "Másolás"}
                testid="email-valueadd-copy" />
              <CreamActionPill onClick={handleDownload}
                icon={DownloadSimple} label="Letöltés"
                testid="email-valueadd-download" />
              <CreamActionPill onClick={handleSave}
                icon={saved ? CheckCircle : DownloadSimple}
                label={savingVault ? "Mentés…" : saved ? "Mentve" : "Mentés a Tudástárba"}
                testid="email-valueadd-save" active={saved}
                disabled={savingVault || saved} />
            </div>

            {hasStructured ? (
              content.emails.map((em, i) => {
                const subjects = em.targysorok || [];
                const chosenIdx = chosenSubjects[em.email_szam] ?? 0;
                const titleFromSubject = (subjects[chosenIdx] || subjects[0] || "").trim();
                return (
                  <ValueAddEmailBlock
                    key={em.email_szam || i}
                    email={em}
                    chosenIdx={chosenIdx}
                    onChoose={(idx) => setChosenSubjects((p) => ({ ...p, [em.email_szam]: idx }))}
                    onCopyOne={() => handleCopyOne(em)}
                    title={titleFromSubject || tema || "Értékadó email"}
                    isFirst={i === 0}
                  />
                );
              })
            ) : (
              <pre style={{
                fontFamily: "inherit", fontSize: 15, lineHeight: 1.75,
                whiteSpace: "pre-wrap", color: "#2a2a35",
              }}>{rawText || ""}</pre>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ───────────────────── EMAIL BLOCK ───────────────────── */

function ValueAddEmailBlock({ email, chosenIdx, onChoose, onCopyOne, title, isFirst }) {
  const { email_szam, targysorok = [], body = "" } = email;

  const subEyebrowStyle = {
    fontSize: 13, color: ACCENT_BLUE, fontWeight: 600,
    letterSpacing: "0.14em", textTransform: "uppercase",
    marginBottom: 12,
  };

  return (
    <div
      data-testid={`email-valueadd-block-${email_szam}`}
      style={{ marginTop: isFirst ? 84 : 0, marginBottom: 0 }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "flex-start",
        gap: 14, marginBottom: 26, flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 21, fontWeight: 600, color: "#0a0a12",
          letterSpacing: "-0.01em", lineHeight: 1.25,
        }}>
          {title}
        </div>
      </div>

      {targysorok.length > 0 && (
        <div style={{ marginBottom: 26 }}>
          <div style={subEyebrowStyle}>Tárgysor-opciók</div>
          <div className="flex flex-wrap gap-2">
            {targysorok.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChoose(i)}
                data-testid={`email-valueadd-${email_szam}-subject-${i}`}
                data-selected={i === chosenIdx ? "true" : "false"}
                style={{
                  fontSize: 14, fontWeight: 400,
                  padding: "8px 16px", borderRadius: 999,
                  background: SUBJECT_PILL_BG,
                  border: `1px solid ${SUBJECT_PILL_BORDER}`,
                  color: "#2a2a35",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.15s, border 0.15s",
                }}
              >{t}</button>
            ))}
          </div>
        </div>
      )}

      <div style={subEyebrowStyle}>Szövegtörzs</div>
      <div
        data-testid={`email-valueadd-${email_szam}-body-card`}
        style={{
          position: "relative",
          background: "#ffffff",
          border: "1px solid rgba(20,30,40,0.06)",
          borderRadius: 10,
          padding: "22px 26px 44px 26px",
          boxShadow: "0 1px 0 rgba(255,255,255,0.40) inset, 0 6px 18px -10px rgba(0,0,0,0.10)",
        }}
      >
        <BodyWithPlaceholders body={body} />
        <button
          type="button"
          onClick={onCopyOne}
          data-testid={`email-valueadd-${email_szam}-copy-one`}
          aria-label="Levél másolása"
          title="Levél másolása"
          style={{
            position: "absolute", right: 10, bottom: 10,
            width: 30, height: 30, borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(20,30,40,0.10)",
            color: ACCENT_BLUE_DARK,
            cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "background 180ms ease, border 180ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = ACCENT_BLUE_TINT_BLOCK;
            e.currentTarget.style.border = `1px solid ${ACCENT_BLUE_BORDER_BLOCK}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.border = "1px solid rgba(20,30,40,0.10)";
          }}
        >
          <CopyIcon size={14} weight="regular" />
        </button>
      </div>
    </div>
  );
}

function BodyWithPlaceholders({ body }) {
  if (!body) return null;
  const parts = [];
  const regex = /\[([^\]\n]{1,160})\]/g;
  let lastIdx = 0;
  let m;
  let keyCounter = 0;
  while ((m = regex.exec(body)) !== null) {
    if (m.index > lastIdx) {
      parts.push({ type: "text", text: body.slice(lastIdx, m.index), key: keyCounter++ });
    }
    const inner = m[1].trim();
    const isPlain = PLAIN_BRACKETS.has(inner.toLowerCase());
    parts.push({
      type: isPlain ? "plain-bracket" : "chip",
      text: inner,
      key: keyCounter++,
    });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) {
    parts.push({ type: "text", text: body.slice(lastIdx), key: keyCounter++ });
  }
  return (
    <div style={{
      fontSize: 15, color: "#2a2a35", lineHeight: 1.75,
      whiteSpace: "pre-wrap", fontFamily: "inherit",
    }}>
      {parts.map((p) => {
        if (p.type === "text") return <React.Fragment key={p.key}>{p.text}</React.Fragment>;
        if (p.type === "plain-bracket") return <React.Fragment key={p.key}>[{p.text}]</React.Fragment>;
        return <PlaceholderChip key={p.key} text={p.text} />;
      })}
    </div>
  );
}

function PlaceholderChip({ text }) {
  const [value, setValue] = useState(text);
  const ref = useRef(null);
  return (
    <span
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={(e) => setValue(e.currentTarget.innerText.trim())}
      data-testid="email-placeholder-chip"
      data-original={text}
      style={{
        display: "inline-block",
        padding: "0 4px",
        margin: "0 1px",
        borderRadius: 4,
        background: "rgba(38, 169, 201, 0.06)",
        boxShadow: "inset 0 -1px 0 rgba(38, 169, 201, 0.22)",
        color: "#1f3a45",
        fontWeight: 500,
        fontSize: "0.97em",
        lineHeight: "inherit",
        cursor: "text",
        outline: "none",
      }}
    >
      {value}
    </span>
  );
}

function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled }) {
  const accentBg = active ? "rgba(29,158,117,0.10)" : ACCENT_BLUE_TINT_PILL;
  const accentBorder = active ? "rgba(29,158,117,0.30)" : "rgba(38, 169, 201,0.35)";
  const accentText = active ? "#1d9e75" : ACCENT_BLUE_DARK;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: accentBg,
        border: `1px solid ${accentBorder}`,
        borderRadius: 999,
        color: accentText,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}
